import { useMemo } from "react";
import {
  emptyScene,
  sceneFromOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import { TacticalBoardView } from "./TacticalBoardView";

export type TacticalBoardThumbnailProps = {
  /** Raw `tactical_board_json` from a note, or a pre-parsed scene. */
  raw?: unknown;
  scene?: BoardScene | null;
  width: number;
  height?: number;
  className: string;
  hideLabels?: boolean;
  /** `field` renders an empty pitch; `label` renders text; `none`
   * returns null when there is no valid board content. */
  fallback?: "field" | "label" | "none";
  fallbackLabel?: string;
  testId?: string;
};

export function TacticalBoardThumbnail({
  raw,
  scene,
  width,
  height,
  className,
  hideLabels = true,
  fallback = "label",
  fallbackLabel = "No board",
  testId = "tactical-board-thumbnail",
}: TacticalBoardThumbnailProps) {
  const parsedScene = useMemo(() => {
    if (scene) return scene;
    if (raw == null) return null;
    const result = sceneFromOnDisk(raw);
    return result.ok ? result.value : null;
  }, [raw, scene]);

  const hasContent =
    parsedScene !== null &&
    (parsedScene.tokens.length > 0 || parsedScene.shapes.length > 0);
  const displayScene = hasContent
    ? parsedScene
    : fallback === "field"
      ? (parsedScene ?? emptyScene())
      : null;

  if (!displayScene && fallback === "none") return null;

  return (
    <span aria-hidden="true" data-testid={testId} className={className}>
      {displayScene ? (
        <TacticalBoardView
          appearance="navy"
          scene={displayScene}
          width={width}
          height={height}
          hideLabels={hideLabels}
          passThrough
        />
      ) : (
        <span className="px-2 text-center font-mono text-[10px] text-ink-muted">
          {fallbackLabel}
        </span>
      )}
    </span>
  );
}

export function tacticalBoardHasContent(raw: unknown): boolean {
  if (raw == null) return false;
  const result = sceneFromOnDisk(raw);
  if (!result.ok) return false;
  return result.value.tokens.length > 0 || result.value.shapes.length > 0;
}
